import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  getRecord,
  getRecordWithLinks,
  getRecordTypeSchema,
  getRecordTypes,
  listAllRecordsByType,
  listAllRecordsWithLinks,
  listAttentionItems,
  transitionRecord,
  getRecordUndo,
  returnCascade,
  MAX_LINK_PAGE,
  ApiError,
  type AttentionItem,
  type LinkRow,
  type RecordRow,
  type RecordTypeSchema,
  type RecordTypeSummary,
  type RecordsWithLinks,
  type RecordUndo,
} from '../lib/api';
import { expiryPhaseFor } from '../lib/cascadeWindow';
import { useRecordsVersion, bumpRecordsVersion } from '../lib/recordEditor';
import { routePath } from '../routes/route';
import { WorkOrderDrawer, type DrawerRequest, CREATE_TYPE_KEYS } from '../components/WorkOrderDrawer';
import { ageInDays, ageLabel } from '../lib/aging';
import { useFrameOwnsBack } from '../components/FrameBack';
import './WorkOrderSpread.css';

// S2 — THE WORK ORDER, AS A SPREAD (wf01, blessed 2026-08-06): their nexus
// screen — the same record AppSheet shows as one long equal-weight scroll,
// ranked by a grid so nothing has to be hunted. Four regions on wf01's own
// grid contract (hero · money · work · rail, the hairline IS the gap),
// reflowing to one column under 900px.
//
// Every number on this screen comes from real records — the WO's own rollup
// fields plus the rows its links reach. Where the port is thinner than the
// wireframe (no margin band lever, no transition-date history read, no
// photo/follow-up record types, no Rexx source-retention path), the region
// renders WITHOUT the missing thing rather than inventing it — the gaps are
// named in the S2 report, not papered over here.

export interface WorkOrderSpreadProps {
  tenantId: string;
  id: string;
  onBack: () => void;
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

const WO_TYPE = 'hvac_work_order';

// The lifecycle SPINE the stepper always shows (spec §1b): the manifest's
// own main chain. This names WHICH states form the spine — the states
// themselves (existence, order, and the off-spine set) are read from the
// FETCHED schema every time, so a manifest change flows through; a state
// named here but absent from the schema simply never renders.
const SPINE = [
  'intake',
  'unassigned',
  'in_progress',
  'awaiting_invoicing',
  'awaiting_payment',
  'awaiting_deposit',
  'deposited',
] as const;

// The work column's panes. The first four are wf01's own tab row (Photos is
// absent — no photo record type exists on this pack, and no generic route
// serves stored file bytes, so a Photos tab could show nothing real). The
// rest are reached from the related rail's pills: wf01 says a pill switches
// the work column's tab, so every linked type gets a pane even though only
// the base four sit in the resting tab row.
type PaneKey =
  | 'work'
  | 'materials'
  | 'notes'
  | 'history'
  | 'quotes'
  | 'invoices'
  | 'payments'
  | 'receipts'
  | 'equipment'
  // CV3: the job's retained captures (hvac_source via source_work_order).
  | 'attachments';

const BASE_TABS: { key: PaneKey; label: string }[] = [
  { key: 'work', label: 'Work' },
  { key: 'materials', label: 'Materials' },
  { key: 'notes', label: 'Notes' },
  { key: 'history', label: 'History' },
];

// The related rail, in wf01's order. Photos and Follow-ups are wf01 pills
// with NO record type behind them on this pack — omitted rather than faked
// (a zero pill that can never be anything but zero is a feature pretended,
// not a feature shown).
const VISIT_TYPE = 'hvac_visit';

const RAIL: { type: string; pane: PaneKey }[] = [
  { type: VISIT_TYPE, pane: 'work' },
  { type: 'hvac_material_line', pane: 'materials' },
  { type: 'hvac_quote', pane: 'quotes' },
  { type: 'hvac_invoice', pane: 'invoices' },
  { type: 'hvac_payment', pane: 'payments' },
  { type: 'hvac_equipment', pane: 'equipment' },
  { type: 'hvac_note', pane: 'notes' },
  { type: 'hvac_receipt', pane: 'receipts' },
  // CV3: what the field captured onto this job — hvac_source rows linked
  // through source_work_order. Landed here, the office sees it seconds
  // after the tech's approve; that visibility IS the chunk.
  { type: 'hvac_source', pane: 'attachments' },
];

function humanize(key: string): string {
  return key.replace(/_/g, ' ');
}

function money(n: number): string {
  return n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function shortDate(iso: string | undefined | null): string {
  if (typeof iso !== 'string' || iso.length === 0) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** The far end of a link, seen from the work order. */
function otherEnd(link: LinkRow, woId: string): { id: string; type: string } {
  return link.src_id === woId
    ? { id: link.dst_id, type: link.dst_type }
    : { id: link.src_id, type: link.src_type };
}

/** A visit's one meaningful date — same reading RexxHome uses. */
function visitWhen(v: RecordRow): string | null {
  const raw = v.fields['actual_start'] ?? v.fields['scheduled_start'];
  return typeof raw === 'string' && raw.length > 0 ? raw : v.created_at;
}

interface SpreadData {
  wo: RecordRow;
  schema: RecordTypeSchema;
  typeLabels: Map<string, RecordTypeSummary>;
  /** Linked rows grouped by record type (both link directions — one read). */
  linked: Map<string, RecordRow[]>;
  client: RecordRow | null;
  site: RecordRow | null;
  /** visit id -> its own linked tech-profile name / source id, read off the
   * visit sweep's own link expansion (S16 — it used to be one request per
   * visit). */
  visitTech: Map<string, string>;
  visitSource: Map<string, string>;
  /** S16: the SERVER's word on whether this work order's link page was cut
   * short. The rail counts every linked row, so a truncated page would
   * under-count the pills — and, before S16, would have done so silently at
   * fifty because no caller ever asked for more or was told. */
  railTruncated: boolean;
  /** S13: the live money-band flags on THIS work order, as the sweep
   * raised them and as THIS viewer is allowed to see them (the two
   * labour-derived flags are owner-scoped — ops/moneyFlags.ts's header).
   * Read from the attention surface rather than recomputed here: the
   * screen must report the flag that was actually raised, not a second
   * opinion that could disagree with the owner's own list. */
  moneyFlags: AttentionItem[];
}

/** The dedup prefix @vowric/core's money-flag sweep owns
 * (ops/moneyFlags.ts's MONEY_FLAG_DEDUP_PREFIX). 'drift' is a broad item
 * type — matching on the prefix is what keeps a future drift emitter's
 * item off the money column. */
const MONEY_FLAG_DEDUP_PREFIX = 'drift:money_flag:';

export function WorkOrderSpread({ tenantId, id, onBack, onNavigate }: WorkOrderSpreadProps) {
  // CV5 §2 — see the breadcrumb below.
  const frameOwnsBack = useFrameOwnsBack();
  const [load, setLoad] = useState<LoadState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [data, setData] = useState<SpreadData | null>(null);
  const [pane, setPane] = useState<PaneKey>('work');
  const [drawer, setDrawer] = useState<DrawerRequest | null>(null);
  const recordsVersion = useRecordsVersion();

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      setLoad('loading');
      try {
        // S16: `link_limit` at the engine's own maximum, and truncation
        // taken as a FACT from the server. The default page is fifty; a
        // busy job with more material lines, notes and receipts than that
        // was having its rail quietly short-counted.
        const {
          record: wo,
          links,
          linksTruncated,
        } = await getRecordWithLinks(tenantId, id, { linkLimit: MAX_LINK_PAGE });

        // Group every link by the type at its far end — the rail is one read
        // (core's listLinks matches src OR dst), and the groups drive both
        // the counts and which type sweeps below are worth filtering.
        const idsByType = new Map<string, Set<string>>();
        for (const l of links) {
          const other = otherEnd(l, wo.id);
          if (other.id === wo.id) continue;
          let set = idsByType.get(other.type);
          if (!set) {
            set = new Set<string>();
            idsByType.set(other.type, set);
          }
          set.add(other.id);
        }

        const clientId = [...(idsByType.get('hvac_client') ?? [])][0];
        const siteId = [...(idsByType.get('hvac_site') ?? [])][0];

        // S16 — THE VISIT SWEEP CARRIES ITS OWN LINKS. This screen already
        // read every visit in the tenant (the rail sweep below), and then
        // read each of THIS job's visits again, one request apiece, purely
        // to learn who worked it. Visits on one job number are in the
        // handfuls, so the fan-out was proportionate rather than dangerous —
        // but S15 made it free to fold into the sweep that was happening
        // anyway (`?include=links`), so it costs nothing to stop.
        const wantVisits = (idsByType.get(VISIT_TYPE)?.size ?? 0) > 0;
        const railTypes = RAIL.filter(({ type }) => type !== VISIT_TYPE);
        const noVisits: RecordsWithLinks = {
          records: [],
          linksByRecordId: {},
          truncated: [],
          unreadable: [],
        };
        const [schema, types, client, site, attention, visitBatch, ...sweeps] = await Promise.all([
          getRecordTypeSchema(tenantId, WO_TYPE),
          getRecordTypes(tenantId),
          // Only the NAME is used from either, so neither needs its links.
          clientId ? getRecord(tenantId, clientId) : Promise.resolve(null),
          siteId ? getRecord(tenantId, siteId) : Promise.resolve(null),
          // S13: the live attention surface, filtered to this record. A
          // reader who cannot see an owner-scoped item simply does not get
          // it back — the read wall is the server's, not a client filter.
          listAttentionItems(tenantId).catch(() => [] as AttentionItem[]),
          wantVisits
            ? listAllRecordsWithLinks(tenantId, VISIT_TYPE, { linkLimit: MAX_LINK_PAGE })
            : Promise.resolve(noVisits),
          ...railTypes.map(({ type }) =>
            (idsByType.get(type)?.size ?? 0) > 0
              ? listAllRecordsByType(tenantId, type)
              : Promise.resolve([] as RecordRow[])
          ),
        ]);

        const linked = new Map<string, RecordRow[]>();
        railTypes.forEach(({ type }, i) => {
          const wanted = idsByType.get(type);
          linked.set(type, wanted ? (sweeps[i] ?? []).filter((r) => wanted.has(r.id)) : []);
        });
        const wantedVisits = idsByType.get(VISIT_TYPE);
        const visits = wantedVisits
          ? visitBatch.records.filter((r) => wantedVisits.has(r.id))
          : [];
        linked.set(VISIT_TYPE, visits);

        // A visit's tech (and any retained source) hang off the VISIT's own
        // links, which the WO read cannot see — they now ride the sweep.
        const visitTech = new Map<string, string>();
        const visitSource = new Map<string, string>();
        if (visits.length > 0) {
          const techs = await listAllRecordsByType(tenantId, 'hvac_tech_profile');
          const techName = new Map(techs.map((t) => [t.id, t.display_name]));
          const unanswered = new Set([...visitBatch.unreadable, ...visitBatch.truncated]);
          for (const visit of visits) {
            if (unanswered.has(visit.id)) continue;
            for (const l of visitBatch.linksByRecordId[visit.id] ?? []) {
              const other = otherEnd(l, visit.id);
              if (other.type === 'hvac_tech_profile') {
                const name = techName.get(other.id);
                if (name) visitTech.set(visit.id, name);
              }
              if (other.type === 'hvac_source') visitSource.set(visit.id, other.id);
            }
          }
        }

        if (cancelled) return;
        setData({
          wo,
          schema,
          typeLabels: new Map(types.map((t) => [t.key, t])),
          linked,
          client,
          site,
          visitTech,
          visitSource,
          moneyFlags: attention.filter(
            (a) => a.objectId === wo.id && a.dedupKey.startsWith(MONEY_FLAG_DEDUP_PREFIX)
          ),
          railTruncated: linksTruncated,
        });
        setLoad('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setLoad('error');
      }
    }
    void loadAll();
    return () => {
      cancelled = true;
    };
  }, [tenantId, id, recordsVersion]);

  // THE STEPPER MOVES THE JOB (Huan, 2026-08-25). It drew the lifecycle as
  // <span>s: it showed where the work order was and could not move it, and
  // the spread's Edit opens the drawer, which renders the form alone and
  // never RecordActions — the component that draws transitions. So a job
  // created in `intake` could never reach `unassigned`, and the dispatch
  // board filters on exactly that state. The core loop was closed by a
  // comment written for a Delete button that no longer exists.
  //
  // The fix is not another actions block: the thing showing your progress is
  // the thing that advances it. A state this record may legally move to is a
  // BUTTON in the same row it already occupies; everything else stays the
  // span it was. The schema already carries {from -> legal next states}, so
  // no new read and no client-side guess about the graph.
  const [moving, setMoving] = useState<string | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);

  const nextStates = useMemo(() => {
    const current = data?.wo.lifecycle_state;
    if (!current) return new Set<string>();
    return new Set(data?.schema.lifecycle?.transitions[current] ?? []);
  }, [data]);

  /** The one obvious next step, and every other legal move.
   *
   * PRIMARY is the next state along the pack's own SPINE — derived from the
   * declaration, never a guess about which route this shop prefers. When the
   * spine's next state is not legal from here (an off-spine state, or a job
   * that skipped ahead), there is no primary and every legal move is offered
   * evenly rather than one being promoted on a hunch. */
  const advance = useMemo(() => {
    const current = data?.wo.lifecycle_state;
    if (!current || nextStates.size === 0) return { primary: null as string | null, others: [] as string[] };
    const onSpine = (SPINE as readonly string[]).indexOf(current);
    const spineNext = onSpine >= 0 ? SPINE[onSpine + 1] : undefined;
    const primary = spineNext && nextStates.has(spineNext) ? spineNext : null;
    return { primary, others: [...nextStates].filter((s) => s !== primary) };
  }, [data, nextStates]);

  async function moveTo(to: string): Promise<void> {
    if (!data || moving) return;
    setMoving(to);
    setMoveError(null);
    try {
      await transitionRecord(tenantId, data.wo.id, to);
      bumpRecordsVersion();
    } catch (err) {
      // The server's own words: a 422 lifecycle_guard and the authority
      // gate's 409 offer both say more than this screen could invent, and
      // "request failed" for a refusal was already a walk defect once.
      setMoveError(err instanceof ApiError ? err.message : 'Could not move it.');
    } finally {
      setMoving(null);
    }
  }

  // GOING BACK (Huan, 2026-08-25). The chain is forward-only by design — no
  // reverse edge exists in the pack's lifecycle, and adding one would make
  // "where is this job" answerable only by reading history. So a mistaken
  // move is not corrected by moving backwards; it is corrected by the move
  // never having happened. That machinery already existed end to end and had
  // never been wired to a screen: the executor records every transition's
  // inverse (restoreState, with the prior state), the manifest grants the
  // work spine a P3D return window, and POST /api/cascades/:id/return
  // un-happens the whole cascade in one transaction.
  //
  // KEYED ON recordsVersion, so it survives a reload. Holding the cascade id
  // from the move would have limited undo to the tab that made it, and the
  // moment someone notices a wrong move is usually not that moment.
  const [undo, setUndo] = useState<RecordUndo | null>(null);
  const [undoing, setUndoing] = useState(false);

  useEffect(() => {
    let live = true;
    // Deliberately outside loadAll: the spread must render even if this
    // fails. No undo offered is a safe degradation; a blank screen is not.
    getRecordUndo(tenantId, id)
      .then((u) => {
        if (live) setUndo(u);
      })
      .catch(() => {
        if (live) setUndo(null);
      });
    return () => {
      live = false;
    };
  }, [tenantId, id, recordsVersion]);

  /** Null unless the window is genuinely open — the mapper's rule, not this
   * screen's: a closed window draws nothing rather than a button certain to
   * 409. The phase it returns also carries the urgency. */
  const undoPhase = useMemo(() => (undo ? expiryPhaseFor(undo.window) : null), [undo]);

  async function undoMove(): Promise<void> {
    if (!undo || undoing) return;
    setUndoing(true);
    setMoveError(null);
    try {
      await returnCascade(tenantId, undo.cascadeId);
      bumpRecordsVersion();
    } catch (err) {
      // A window that closed between the draw and the click answers 409 with
      // the reason — entanglement names what leans on it. Say that, not
      // "failed".
      setMoveError(err instanceof ApiError ? err.message : 'Could not undo it.');
    } finally {
      setUndoing(false);
    }
  }

  const stepper = useMemo(() => {
    if (!data) return [];
    const states = data.schema.lifecycle?.states ?? [];
    const current = data.wo.lifecycle_state;
    // The spine, in the schema's own order; the current state, when it is an
    // off-spine one, is INSERTED at its schema-order position rather than
    // always shown (spec §1b — off-spine states appear only when the record
    // is actually in one).
    const spineSet = new Set<string>(SPINE);
    const shown = states.filter((s) => spineSet.has(s) || s === current);
    const currentIdx = current ? shown.indexOf(current) : -1;
    return shown.map((s, i) => ({
      state: s,
      status: currentIdx === -1 ? 'todo' : i < currentIdx ? 'done' : i === currentIdx ? 'now' : 'todo',
      offSpine: !spineSet.has(s),
    }));
  }, [data]);

  if (load === 'loading') {
    return (
      <div class="wo-spread">
        <div class="wo-spread__eyebrow">Work order</div>
        <div class="wo-spread__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' || !data) {
    return (
      <div class="wo-spread">
        <div class="wo-spread__eyebrow">Work order</div>
        <div class="wo-spread__say">
          Couldn't load this work order.
          <br />
          <span class="wo-spread__say-soft">{errorMessage}</span>
        </div>
      </div>
    );
  }

  const { wo, client, site, linked, typeLabels, visitTech, visitSource, moneyFlags } = data;
  const f = wo.fields;

  const visits = [...(linked.get('hvac_visit') ?? [])].sort((a, b) =>
    (visitWhen(a) ?? '') < (visitWhen(b) ?? '') ? 1 : -1
  );
  const materials = linked.get('hvac_material_line') ?? [];
  const invoices = linked.get('hvac_invoice') ?? [];
  const payments = linked.get('hvac_payment') ?? [];
  const notes = linked.get('hvac_note') ?? [];
  // CV3: every hvac_source linked to this job, whichever relationship
  // carried it — the new source_work_order lane and the legacy
  // cardinality-one `source` slot are both retained originals on this job.
  const attachments = linked.get('hvac_source') ?? [];

  // ---- money: every figure from real rows/rollups (spec §1c) ----
  const labourBilled = num(f['visit_billed_amount_sum']);
  const labourHours = num(f['visit_hours_actual_sum']);
  const materialsBilled = materials.reduce(
    (s, m) => s + num(m.fields['qty']) * num(m.fields['selling_price_per_unit']),
    0
  );
  const materialsCost = materials.reduce(
    (s, m) => s + num(m.fields['qty']) * num(m.fields['purchased_price_per_unit']),
    0
  );
  const travel = visits.reduce((s, v) => s + num(v.fields['travel']), 0);
  const invoiced = num(f['invoice_total_sum']);
  // Received: the linked invoices' own amount_paid rollups are canonical;
  // only a WO with no invoice at all falls back to directly-linked payments
  // (so a payment counted through its invoice is never counted twice).
  const received =
    invoices.length > 0
      ? invoices.reduce((s, inv) => s + num(inv.fields['amount_paid']), 0)
      : payments.reduce((s, p) => s + num(p.fields['amount']), 0);
  const outstanding = invoiced - received;

  // Materials margin, computed from the material lines' own cost/sell
  // fields — both exist on this manifest, so the number is real. Still
  // rendered as a PLAIN kv with no verdict attached: this is the MATERIALS
  // margin, not the job's overall one, and the verdict now belongs to the
  // flagline below, which reports what the sweep actually raised rather
  // than a second opinion computed on the client.
  const marginLines = materials.filter(
    (m) =>
      m.fields['selling_price_per_unit'] !== undefined &&
      m.fields['selling_price_per_unit'] !== null &&
      m.fields['purchased_price_per_unit'] !== undefined &&
      m.fields['purchased_price_per_unit'] !== null
  );
  const marginSell = marginLines.reduce(
    (s, m) => s + num(m.fields['qty']) * num(m.fields['selling_price_per_unit']),
    0
  );
  const marginCost = marginLines.reduce(
    (s, m) => s + num(m.fields['qty']) * num(m.fields['purchased_price_per_unit']),
    0
  );
  const margin = marginLines.length > 0 && marginSell > 0 ? ((marginSell - marginCost) / marginSell) * 100 : null;

  // S13 — THE FLAGLINE (wf01, blessed): "Margin 31.4% — below the 35%
  // band. Flagged to Alan Jun 22." S2 left it out because no band existed;
  // now it does, and every part of the sentence comes from the raised item
  // rather than from this screen: the verdict is the sweep's `reason`, the
  // date is the item's own created_at, and the name is the owner the
  // exception was routed to (absent when the tenant never recorded one —
  // then the line simply says when, not to whom, rather than inventing a
  // person). Ordered so the same job always reads the same way.
  const flaglines = [...moneyFlags]
    .sort((a, b) => (String(a.sourceRef?.['flag'] ?? '') < String(b.sourceRef?.['flag'] ?? '') ? -1 : 1))
    .map((item) => {
      const who = typeof item.sourceRef?.['flaggedTo'] === 'string' ? (item.sourceRef['flaggedTo'] as string) : null;
      const when = shortDate(item.createdAt);
      // wf01 bolds the measure ("Margin 31.4%") and runs the verdict plain.
      // The sweep writes the two halves either side of an em dash, so the
      // screen splits there rather than counting words (a two-word flag
      // name would break a word count, and one of the three has one).
      const lead = (item.reason ?? '').trim();
      const cut = lead.indexOf(' — ');
      return {
        id: item.id,
        strong: cut === -1 ? lead : lead.slice(0, cut),
        tail: cut === -1 ? '' : lead.slice(cut + 1),
        flagged: who ? `Flagged to ${who} ${when}.` : `Flagged ${when}.`,
      };
    });

  const latestInvoice = invoices.length > 0 ? invoices[invoices.length - 1]! : null;
  const singleInvoiceId = invoices.length === 1 ? invoices[0]!.id : undefined;

  // S14 — wf01's own line, "unpaid 41 days", which S2 could not draw
  // because hvac_invoice had no date. It does now (issued_date), and the
  // arithmetic is lib/aging.ts's, so this screen and the home count days
  // the same way.
  //
  // THE OLD LINE WAS WRONG, not merely missing: the Invoiced kv read
  // `latestInvoice.created_at`, which is the day the row was WRITTEN. On
  // every seeded tenant and on all 2,756 imported V6 invoices that is the
  // day the import ran, so the screen was already stating an invoice date
  // — just not a true one. It now reads the field the business fills in,
  // and where nobody filled it in it says so instead of substituting a
  // timestamp.
  const invoiceIssued = latestInvoice ? latestInvoice.fields['issued_date'] : null;
  const invoicedLabel = latestInvoice
    ? shortDate(str(invoiceIssued))
      ? `Invoiced ${shortDate(str(invoiceIssued))}`
      : 'Invoiced · date unknown'
    : 'Invoiced';
  // Only money still owed has an unpaid age; a settled job says nothing.
  const unpaidAge = outstanding > 0.005 && latestInvoice ? ageInDays(invoiceIssued) : null;
  const unpaidLine =
    outstanding > 0.005 && latestInvoice
      ? unpaidAge === null
        ? 'unpaid · age unknown'
        : `unpaid ${ageLabel(unpaidAge)}`
      : null;

  const ageDays = Math.max(0, Math.round((Date.now() - new Date(wo.created_at).getTime()) / 86_400_000));

  const woRef = [str(f['wo_number']) || wo.display_name, humanize(str(f['job_type']))]
    .filter(Boolean)
    .join(' · ');
  const scopeLabel = [str(f['wo_number']) || wo.display_name, site?.display_name ?? client?.display_name]
    .filter(Boolean)
    .join(' · ');

  const siteBits = [
    site?.display_name,
    str(f['caller']) || str(site?.fields['on_site_contact_name'] ?? ''),
    `opened ${shortDate(wo.created_at)}`,
  ].filter(Boolean);

  const tabs: { key: PaneKey; label: string }[] = BASE_TABS.some((t) => t.key === pane)
    ? BASE_TABS
    : [...BASE_TABS, { key: pane, label: humanize(pane).replace(/^./, (c) => c.toUpperCase()) }];

  function pillLabel(type: string, count: number): string {
    const t = typeLabels.get(type);
    if (!t) return humanize(type.replace(/^hvac_/, ''));
    return count === 1 ? t.label : t.labelPlural;
  }

  function listPane(rows: RecordRow[], typeKey: string, line: (r: RecordRow) => string): preact.JSX.Element {
    if (rows.length === 0) {
      return <div class="wo-spread__empty">Nothing here yet.</div>;
    }
    return (
      <div>
        {rows.map((r) => (
          <button
            type="button"
            class="wo-spread__lrow"
            key={r.id}
            onClick={() => setDrawer({ mode: 'edit', typeKey, id: r.id })}
          >
            <b>{r.display_name}</b>
            <span>{line(r)}</span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div class="wo-spread">
      {/* The breadcrumb climbs — client › site › WO — and is clickable the
          whole way up. The client/site paths are S4's routes: until its
          drilldowns land they resolve to S1's clients placeholder, which is
          the agreed seam (spec §1a). */}
      <nav class="wo-spread__crumb" aria-label="Breadcrumb">
        {/* CV5 §2 — the FIRST crumb is this screen's way back to its list,
            and on the phone frame the frame renders that itself. Dropping
            just this one leaf keeps the breadcrumb doing its other job (it
            still climbs client › site, and still says where you are) without
            putting two "up to Work orders" controls on one screen. */}
        {frameOwnsBack ? null : (
          <button type="button" onClick={onBack}>
            Work orders
          </button>
        )}
        {client ? (
          <>
            {frameOwnsBack ? null : (
              <span class="wo-spread__crumb-sep" aria-hidden="true">
                ›
              </span>
            )}
            <button type="button" onClick={() => onNavigate(`/clients/detail/${client.id}`)}>
              {client.display_name}
            </button>
          </>
        ) : null}
        {site ? (
          <>
            <span class="wo-spread__crumb-sep" aria-hidden="true">
              ›
            </span>
            <button type="button" onClick={() => onNavigate(`/clients/site/${site.id}`)}>
              {site.display_name}
            </button>
          </>
        ) : null}
        <span class="wo-spread__crumb-sep" aria-hidden="true">
          ›
        </span>
        <span class="wo-spread__crumb-cur">{str(f['wo_number']) || wo.display_name}</span>
      </nav>

      <div class="wo-spread__grid">
        {/* ---- HERO — the client is the subject; the WO number is a
                reference in the label line (spec §1a) ---- */}
        <section class="wo-spread__hero" aria-label="Work order">
          <div class="wo-spread__hero-top">
            <div>
              <span class="wo-spread__label">{woRef}</span>
              {f['urgent'] === true ? <span class="wo-spread__urgent">Urgent</span> : null}
              <h3 class="wo-spread__client">{client?.display_name ?? wo.display_name}</h3>
              {siteBits.length > 0 ? <div class="wo-spread__site">{siteBits.join(' · ')}</div> : null}
            </div>
            <div class="wo-spread__hero-acts">
              {/* CV2 §4 — the office's door to this job's own conversation
                  (the same thread the tech's Chat tab reaches). */}
              <button
                type="button"
                class="wo-spread__edit"
                data-testid="wos-chat-door"
                onClick={() =>
                  onNavigate(routePath({ screen: 'conversation', lane: 'job', id: wo.id }))
                }
              >
                Chat
              </button>
              <button
                type="button"
                class="wo-spread__edit"
                onClick={() => setDrawer({ mode: 'edit', typeKey: WO_TYPE, id: wo.id })}
              >
                Edit
              </button>
            </div>
          </div>
          <div class="wo-spread__stepper">
            {stepper.map((s) => (
              <span
                key={s.state}
                class={`wo-spread__step${
                  s.status === 'now'
                    ? s.offSpine
                      ? ' wo-spread__step--gate'
                      : ' wo-spread__step--now'
                    : s.status === 'done'
                      ? ' wo-spread__step--done'
                      : ''
                }`}
              >
                {humanize(s.state)}
                {/* Only the current step carries a sub-line: the age, the
                    number that is the whole reason the record is open. The
                    dates each PAST gate was passed live in audit history no
                    web read exposes yet — that gap is named in the report,
                    not faked here. */}
                {s.status === 'now' ? (
                  <span class="wo-spread__step-d">{ageDays === 1 ? '1 day' : `${ageDays} days`}</span>
                ) : null}
              </span>
            ))}
          </div>

          {/* THE NEXT STEP IS A BUTTON YOU CAN SEE (Huan, 2026-08-25: "make
              the next step more obviously a button to push… right now it
              looks a bit like that area failed to render properly").
              
              It did look broken, and for a reason worth keeping: `in_progress`
              has FOUR legal moves — on down the chain, the small-job skip to
              invoicing, and the two exits — so four dashed outlines wrapped
              into each other inside a stepper never sized for them. A progress
              spine and an action are two different jobs; the spine above is
              read-only again, and the acting happens here.
              
              PRIMARY is the next state along the pack's own declared spine —
              derivable, not a guess about which route this shop prefers.
              Everything else legal stays reachable and quieter: the skip-ahead
              and the two exits are real choices, not the everyday one. */}
          {advance.primary || advance.others.length > 0 ? (
            <div class="wo-spread__advance">
              {advance.primary ? (
                <button
                  type="button"
                  class="wo-spread__go"
                  disabled={moving !== null}
                  onClick={() => void moveTo(advance.primary!)}
                >
                  {moving === advance.primary ? 'Moving…' : `Move to ${humanize(advance.primary)}`}
                </button>
              ) : null}
              {advance.others.length > 0 ? (
                <div class="wo-spread__advance-alts">
                  {advance.others.map((st) => (
                    <button
                      key={st}
                      type="button"
                      class="wo-spread__go-alt"
                      disabled={moving !== null}
                      onClick={() => void moveTo(st)}
                    >
                      {moving === st ? 'Moving…' : humanize(st)}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {/* OUTSIDE the advance conditional on purpose: a job with nowhere
              left to go forward (cancelled, deposited) is exactly the one
              most worth being able to take back. */}
          {undo && undoPhase ? (
            <div class="wo-spread__undo" data-phase={undoPhase}>
              <button
                type="button"
                class="wo-spread__undo-go"
                disabled={undoing || moving !== null}
                onClick={() => void undoMove()}
              >
                {undoing
                  ? 'Undoing…'
                  : undo.priorState
                    ? `Undo \u2014 back to ${humanize(undo.priorState)}`
                    : 'Undo the last move'}
              </button>
            </div>
          ) : null}
          {moveError !== null ? (
            <div class="wo-spread__move-error" role="alert">
              {moveError}
            </div>
          ) : null}
        </section>

        {/* ---- MONEY — a column, not a footer (spec §1c) ---- */}
        <section class="wo-spread__money" aria-label="Money">
          <div class="wo-spread__money-h">
            <span class="wo-spread__label">Outstanding</span>
            {latestInvoice ? <span class="wo-spread__label">{latestInvoice.display_name}</span> : null}
          </div>
          <div
            class="wo-spread__big"
            style={outstanding > 0 ? { color: 'var(--status-critical)' } : undefined}
          >
            ${money(outstanding)}
          </div>
          {unpaidLine ? (
            <div
              class="wo-spread__unpaid"
              style={
                unpaidAge !== null && unpaidAge > 60 ? { color: 'var(--status-critical)' } : undefined
              }
            >
              {unpaidLine}
            </div>
          ) : null}
          <div class="wo-spread__kvs">
            <div class="wo-spread__kv">
              <span>Labour · {labourHours} h</span>
              <b>{money(labourBilled)}</b>
            </div>
            <div class="wo-spread__kv">
              <span>
                Materials · {materials.length === 1 ? '1 line' : `${materials.length} lines`}
              </span>
              <b>{money(materialsBilled)}</b>
            </div>
            <div class="wo-spread__kv">
              <span>Materials cost</span>
              <b>{money(materialsCost)}</b>
            </div>
            <div class="wo-spread__kv">
              <span>Travel</span>
              <b>{money(travel)}</b>
            </div>
            <div class="wo-spread__kv wo-spread__kv--total">
              <span>{invoicedLabel}</span>
              <b>{money(invoiced)}</b>
            </div>
            <div class="wo-spread__kv">
              <span>Received</span>
              <b>{money(received)}</b>
            </div>
            {margin !== null ? (
              <div class="wo-spread__kv">
                <span>Materials margin</span>
                <b>{margin.toFixed(1)}%</b>
              </div>
            ) : null}
          </div>
          {flaglines.map((line) => (
            <div class="wo-spread__flagline" key={line.id}>
              <span class="wo-spread__flagline-stripe" aria-hidden="true" />
              <span>
                <b>{line.strong}</b>
                {line.tail ? ` ${line.tail}` : ''} {line.flagged}
              </span>
            </div>
          ))}
          <div class="wo-spread__acts">
            <button
              type="button"
              onClick={() => setDrawer({ mode: 'create', typeKey: 'hvac_payment' })}
            >
              Record payment
            </button>
            {latestInvoice ? (
              <button
                type="button"
                class="wo-spread__act-ghost"
                onClick={() => setDrawer({ mode: 'edit', typeKey: 'hvac_invoice', id: latestInvoice.id })}
              >
                Open invoice
              </button>
            ) : null}
          </div>
        </section>

        {/* ---- WORK — tabs, and the first one is the story (spec §1d) ---- */}
        <section class="wo-spread__work" aria-label="Work">
          <div class="wo-spread__tabs" role="tablist">
            {tabs.map((t) => (
              <button
                type="button"
                role="tab"
                key={t.key}
                class="wo-spread__tab"
                aria-selected={pane === t.key}
                onClick={() => setPane(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {pane === 'work' ? (
            <div>
              <p class="wo-spread__issue">
                {str(f['title'])}
                {str(f['supplemental_issues']) ? ` — ${str(f['supplemental_issues'])}` : ''}
              </p>
              {visits.length === 0 ? (
                <div class="wo-spread__empty">No visits yet.</div>
              ) : (
                visits.map((v) => {
                  const tech = visitTech.get(v.id);
                  const sourceId = visitSource.get(v.id);
                  const hours = num(v.fields['hours_actual']);
                  return (
                    <div class="wo-spread__visit" key={v.id}>
                      <span
                        class="wo-spread__visit-m"
                        style={{
                          background:
                            v.lifecycle_state === 'completed'
                              ? 'var(--status-good)'
                              : 'var(--status-neutral)',
                        }}
                      />
                      <div>
                        <button
                          type="button"
                          class="wo-spread__visit-hd"
                          onClick={() => setDrawer({ mode: 'edit', typeKey: 'hvac_visit', id: v.id })}
                        >
                          <b>
                            {tech ? `${tech} · ` : ''}
                            {humanize(v.lifecycle_state ?? 'visit')}
                          </b>
                          <span class="wo-spread__visit-t">
                            {shortDate(visitWhen(v))}
                            {hours > 0 ? ` · ${hours} h` : ''}
                          </span>
                        </button>
                        {str(v.fields['work_done']) ? (
                          <div class="wo-spread__visit-body">{str(v.fields['work_done'])}</div>
                        ) : null}
                        {/* Provenance: a chip renders ONLY when the visit
                            really links a retained source — a visit with no
                            source link gets no chip, never a fake one. */}
                        {sourceId ? (
                          <button
                            type="button"
                            class="wo-spread__prov"
                            onClick={() =>
                              onNavigate(
                                routePath({
                                  screen: 'source',
                                  id: sourceId,
                                  from: routePath({ screen: 'workOrderDetail', id: wo.id }),
                                })
                              )
                            }
                          >
                            <span class="wo-spread__prov-d" />
                            Source
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          ) : null}

          {pane === 'materials'
            ? listPane(materials, 'hvac_material_line', (m) => {
                const qty = num(m.fields['qty']);
                const sell = num(m.fields['selling_price_per_unit']);
                const bits = [
                  str(m.fields['supplier']),
                  qty > 0 && sell > 0 ? `${qty} × $${money(sell)}` : '',
                ].filter(Boolean);
                return bits.join(' · ');
              })
            : null}

          {pane === 'notes'
            ? notes.length === 0
              ? <div class="wo-spread__empty">Nothing here yet.</div>
              : (
                <div>
                  {notes.map((n) => (
                    <button
                      type="button"
                      class="wo-spread__lrow"
                      key={n.id}
                      onClick={() => setDrawer({ mode: 'edit', typeKey: 'hvac_note', id: n.id })}
                    >
                      <b>{str(n.fields['body']) || n.display_name}</b>
                      <span>{shortDate(n.created_at)}</span>
                    </button>
                  ))}
                </div>
              )
            : null}

          {/* CV3 — ATTACHMENTS: the job's retained captures, rendered like
              the notes pane. A row opens the source viewer (the original
              file in its original form), not the record editor — the
              retained original is the fact, and the viewer is its door. */}
          {pane === 'attachments'
            ? attachments.length === 0
              ? <div class="wo-spread__empty">Nothing here yet.</div>
              : (
                <div>
                  {attachments.map((s) => (
                    <button
                      type="button"
                      class="wo-spread__lrow"
                      key={s.id}
                      onClick={() =>
                        onNavigate(
                          routePath({
                            screen: 'source',
                            id: s.id,
                            from: routePath({ screen: 'workOrderDetail', id: wo.id }),
                          })
                        )
                      }
                    >
                      <b>{str(s.fields['filename']) || str(s.fields['raw_text']) || s.display_name}</b>
                      <span>
                        {[str(s.fields['kind']), shortDate(str(s.fields['received_at']) || s.created_at)]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </button>
                  ))}
                </div>
              )
            : null}

          {pane === 'history' ? (
            <div>
              {/* What the web can honestly read today: the record's own dated
                  facts. Per-transition history exists server-side (audit) but
                  no read surfaces it — named in the report. */}
              <div class="wo-spread__kv">
                <span>Opened</span>
                <b>{shortDate(wo.created_at)}</b>
              </div>
              <div class="wo-spread__kv">
                <span>Last touched</span>
                <b>{shortDate(wo.updated_at)}</b>
              </div>
              <div class="wo-spread__kv">
                <span>State</span>
                <b>{humanize(wo.lifecycle_state ?? '')}</b>
              </div>
            </div>
          ) : null}

          {pane === 'quotes'
            ? listPane(linked.get('hvac_quote') ?? [], 'hvac_quote', (q) =>
                humanize(q.lifecycle_state ?? '')
              )
            : null}
          {pane === 'invoices'
            ? listPane(invoices, 'hvac_invoice', (inv) =>
                [
                  humanize(inv.lifecycle_state ?? ''),
                  // S14: absent rather than faked — an invoice with no
                  // issued date simply carries no date on its line.
                  shortDate(str(inv.fields['issued_date']))
                    ? `issued ${shortDate(str(inv.fields['issued_date']))}`
                    : '',
                  `paid $${money(num(inv.fields['amount_paid']))}`,
                ]
                  .filter(Boolean)
                  .join(' · ')
              )
            : null}
          {pane === 'payments'
            ? // S14: the day the money ARRIVED (received_date), not the day
              // the row was written. Same correction as the Invoiced kv
              // above, and the same honesty where the field is empty.
              listPane(payments, 'hvac_payment', (p) =>
                shortDate(str(p.fields['received_date'])) || 'date unknown'
              )
            : null}
          {pane === 'receipts'
            ? listPane(linked.get('hvac_receipt') ?? [], 'hvac_receipt', (r) =>
                [humanize(r.lifecycle_state ?? ''), `$${money(num(r.fields['amount']))}`]
                  .filter(Boolean)
                  .join(' · ')
              )
            : null}
          {pane === 'equipment'
            ? listPane(linked.get('hvac_equipment') ?? [], 'hvac_equipment', (e) =>
                str(e.fields['serial'])
              )
            : null}
        </section>

        {/* ---- THE RELATED RAIL — everything, including zeros (spec §1e) ---- */}
        <section class="wo-spread__rail" aria-label="On this work order">
          <div class="wo-spread__rail-row">
            <span class="wo-spread__label wo-spread__rail-lbl">On this work order</span>
            {RAIL.map(({ type, pane: target }) => {
              const count = (linked.get(type) ?? []).length;
              return (
                <button
                  type="button"
                  key={type}
                  class={`wo-spread__pill${count === 0 ? ' wo-spread__pill--zero' : ''}${
                    pane === target ? ' wo-spread__pill--on' : ''
                  }`}
                  onClick={() => setPane(target)}
                >
                  <b>{count}</b> {pillLabel(type, count)}
                </button>
              );
            })}
            <button
              type="button"
              class="wo-spread__add"
              aria-haspopup="dialog"
              onClick={() => setDrawer({ mode: 'create', typeKey: CREATE_TYPE_KEYS[0] })}
            >
              + Add
            </button>
          </div>
          {data.railTruncated ? (
            // S16: a count that is short says so. It cannot be reached by
            // any job Ether Inc has today — two hundred linked rows on one
            // work order — but a pill that quietly under-counts is exactly
            // the silent shortfall this tranche exists to end.
            <div class="wo-spread__rail-row wo-spread__rail-short">
              This job has more linked records than one read returns — the counts above are at
              least what they say.
            </div>
          ) : null}
        </section>
      </div>

      <WorkOrderDrawer
        tenantId={tenantId}
        woId={wo.id}
        scopeLabel={scopeLabel}
        invoiceId={singleInvoiceId}
        request={drawer}
        onSwitch={setDrawer}
        onClose={() => setDrawer(null)}
      />
    </div>
  );
}
